import { clsx } from "clsx";
import { ReactNode, Ref } from "react";

import expandIcon from "../../assets/expand.svg";
import { LoadingDots } from "./LoadingDots.tsx";
import Markdown from "./Markdown.tsx";

export function ChatMessageHolder({
  children,
  className,
  scrollRef,
}: {
  children: ReactNode;
  className?: string;
  scrollRef?: Ref<HTMLDivElement>;
}) {
  return (
    <div
      className={clsx(
        "flex flex-col overflow-y-auto gap-2 p-2 scrollbar-thin scrollbar-track-white scrollbar-thumb-slate-100",
        className,
      )}
      ref={scrollRef}
    >
      {children}
    </div>
  );
}

export enum ChatMessageType {
  QUESTION = "QUESTION",
  RESPONSE = "RESPONSE",
  RESPONSE_IN_PROCESS = "RESPONSE_IN_PROCESS",
}

export function ChatMessage({
  type,
  className,
  message,
  children,
  onImageClick,
  loadingLabel,
}: {
  type: ChatMessageType;
  message?: string;
  className?: string;
  children?: ReactNode;
  onImageClick?: (src: string) => void;
  // Shown beside the dots until the first words arrive.
  loadingLabel?: string;
}) {
  const isResponseInProcess = type === ChatMessageType.RESPONSE_IN_PROCESS;
  const showLoadingState = isResponseInProcess && !message;

  return (
    <div
      className={clsx("p-2 rounded max-w-[80%] gap-6 prose prose-sm prose-neutral prose-img:m-0", className, {
        "self-start": type === ChatMessageType.RESPONSE || type === ChatMessageType.RESPONSE_IN_PROCESS,
        "self-end whitespace-pre-wrap": type === ChatMessageType.QUESTION,
        "bg-orange-200": type === ChatMessageType.RESPONSE,
        "bg-slate-200": type === ChatMessageType.QUESTION,
        "bg-orange-100": type === ChatMessageType.RESPONSE_IN_PROCESS,
      })}
    >
      {showLoadingState ? (
        loadingLabel ? (
          <span className="flex items-baseline gap-1 text-gray-600">
            {loadingLabel}
            <LoadingDots />
          </span>
        ) : (
          <LoadingDots />
        )
      ) : (
        message && (
          <Markdown
            gfm
            components={{
              a: ({ href, children }) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline hover:text-blue-800"
                >
                  {children}
                </a>
              ),
              table: ({ children }) => (
                <div className="overflow-x-auto my-2">
                  <table className="min-w-full text-sm border-collapse border border-slate-300">{children}</table>
                </div>
              ),
              thead: ({ children }) => <thead className="bg-slate-100">{children}</thead>,
              th: ({ children }) => (
                <th className="px-3 py-2 text-left font-medium border border-slate-300">{children}</th>
              ),
              td: ({ children }) => <td className="px-3 py-2 border border-slate-300">{children}</td>,
              img: ({ src, alt }) => {
                // Don't render images during streaming to prevent flickering
                if (isResponseInProcess) {
                  return null;
                }
                const handleImageClick = () => {
                  if (src && onImageClick) {
                    onImageClick(src);
                  }
                };
                return (
                  <div className="relative">
                    <img
                      src={src}
                      alt={alt}
                      onClick={handleImageClick}
                      className="max-w-full h-auto rounded cursor-pointer"
                    />
                    {onImageClick && (
                      <div
                        onClick={handleImageClick}
                        className="absolute top-1 right-1 rounded p-0.5 cursor-pointer bg-gray-200/90"
                      >
                        <img src={expandIcon} alt="expand" className="w-5 h-5 text-gray-700" />
                      </div>
                    )}
                  </div>
                );
              },
            }}
          >
            {message}
          </Markdown>
        )
      )}
      {children}
    </div>
  );
}
